import { Modal, TextInput, Stack, Group, Button, Text, ActionIcon } from '@mantine/core';
import { IconLink, IconMoodSmile, IconX } from '@tabler/icons-react';
import { useAtom } from 'jotai';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { notifications } from '@mantine/notifications';
import { editLinkAtom, EditLinkState } from '@/features/page/tree/atoms/edit-link-atom';
import { useUpdatePageMutation } from '@/features/page/queries/page-query';
import { treeDataAtom } from '@/features/page/tree/atoms/tree-data-atom';
import type { SpaceTreeNode } from '@/features/page/tree/types';

function patchTreeNode(
  nodes: SpaceTreeNode[],
  id: string,
  patch: Partial<SpaceTreeNode>,
): SpaceTreeNode[] {
  return nodes.map((n) => {
    if (n.id === id) return { ...n, ...patch };
    if (n.children && n.children.length > 0) {
      return { ...n, children: patchTreeNode(n.children, id, patch) };
    }
    return n;
  });
}

export function EditLinkDialog() {
  const { t } = useTranslation();
  // @ts-ignore — jotai generic inference trips with strictNullChecks=false
  const [state, setState] = useAtom<EditLinkState | null, [EditLinkState | null], void>(editLinkAtom);
  // @ts-ignore — same reason
  const [treeData, setTreeData] = useAtom<SpaceTreeNode[], [SpaceTreeNode[]], void>(treeDataAtom);
  const [title, setTitle] = useState('');
  const [url, setUrl] = useState('');
  const updatePage = useUpdatePageMutation();

  useEffect(() => {
    if (state) {
      setTitle(state.title);
      setUrl(state.url);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state?.pageId]);

  const close = () => setState(null);

  const save = () => {
    if (!state || !url.trim()) return;
    const payload: Record<string, unknown> = { pageId: state.pageId };
    if (title !== state.title) payload.title = title;
    if (url.trim() !== state.url) payload.externalUrl = url.trim();
    if (Object.keys(payload).length === 1) {
      close();
      return;
    }
    updatePage.mutate(payload as any, {
      onSuccess: () => {
        // Optimistically patch the in-memory sidebar tree so admin UI reflects change.
        const patch: Partial<SpaceTreeNode> = {};
        if (payload.title !== undefined) patch.name = payload.title as string;
        if (payload.externalUrl !== undefined) patch.externalUrl = payload.externalUrl as string;
        if (Object.keys(patch).length > 0 && state) {
          setTreeData(patchTreeNode(treeData || [], state.pageId, patch));
        }
        notifications.show({ message: t('Link updated') });
        close();
      },
      onError: () =>
        notifications.show({
          color: 'red',
          message: t('Failed to update link'),
        }),
    });
  };

  return (
    <Modal
      opened={state !== null}
      onClose={close}
      title={<Text fw={600}>{t('Edit link')}</Text>}
      centered
    >
      <Stack gap="md">
        <Text size="xs" c="dimmed">
          {t('Describe the link content for your readers and also improved SEO')}
        </Text>
        <TextInput
          label={t('Title')}
          placeholder={t('Link title')}
          value={title}
          onChange={(e) => setTitle(e.currentTarget.value)}
          leftSection={
            <ActionIcon variant="subtle" c="gray" size="sm" aria-label={t('Emoji')}>
              <IconMoodSmile size={16} />
            </ActionIcon>
          }
        />
        <TextInput
          label={t('URL or content')}
          placeholder="https://example.com"
          value={url}
          onChange={(e) => setUrl(e.currentTarget.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              save();
            }
          }}
          leftSection={<IconLink size={16} />}
          rightSection={
            url ? (
              <ActionIcon
                variant="subtle"
                c="gray"
                size="sm"
                onClick={() => setUrl('')}
                aria-label={t('Clear')}
              >
                <IconX size={14} />
              </ActionIcon>
            ) : null
          }
          required
        />
        <Group justify="flex-end">
          <Button variant="default" onClick={close}>
            {t('Cancel')}
          </Button>
          <Button
            onClick={save}
            disabled={!url.trim()}
            loading={updatePage.isPending}
          >
            {t('Save')}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
